// In-memory, singleton context for observability.
// Mirrors backend Logback MDC: fields that should automatically appear on every log.
//
// Phase 1.4 scaffold — module is self-contained; no callers yet.
// Later phases (Track 3) wire Redux auth/store slices into `logContext.set`.

import { logEnv } from './env';

export type LogContextSnapshot = {
  sessionId: string;
  userId: string | null;
  role: string | null;
  storeUUID: string | null;
  mallUUID: string | null;
  brandUUID: string | null;
  appVersion: string;
  buildSha: string;
  env: 'dev' | 'staging' | 'prod';
  clientType: 'web';
};

const SESSION_STORAGE_KEY = 'zopeo_log_session';

function safeSessionStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    // Probe to catch privacy modes that throw on access.
    const probe = '__zopeo_probe__';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function makeSessionId(): string {
  const store = safeSessionStorage();
  if (store) {
    const existing = store.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
  }
  const id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  if (store) store.setItem(SESSION_STORAGE_KEY, id);
  return id;
}

const ctx: LogContextSnapshot = {
  sessionId: makeSessionId(),
  userId: null,
  role: null,
  storeUUID: null,
  mallUUID: null,
  brandUUID: null,
  appVersion: logEnv.appVersion,
  buildSha: logEnv.buildSha,
  env: logEnv.env,
  clientType: 'web',
};

export const logContext = {
  /** Snapshot of the current context. Returns a defensive copy. */
  snapshot(): LogContextSnapshot {
    return { ...ctx };
  },
  /** Patch one or more fields. Used by auth/store-slice subscribers. */
  set(patch: Partial<LogContextSnapshot>): void {
    Object.assign(ctx, patch);
  },
  /** Clear user-scoped fields on logout. Session/build/env fields are kept. */
  clearUser(): void {
    ctx.userId = null;
    ctx.role = null;
    ctx.storeUUID = null;
    ctx.mallUUID = null;
    ctx.brandUUID = null;
  },
  /** Test-only: fully reset including sessionId. Not exported via public surface. */
  __resetForTests(): void {
    const store = safeSessionStorage();
    if (store) store.removeItem(SESSION_STORAGE_KEY);
    ctx.sessionId = makeSessionId();
    ctx.userId = null;
    ctx.role = null;
    ctx.storeUUID = null;
    ctx.mallUUID = null;
    ctx.brandUUID = null;
    ctx.appVersion = logEnv.appVersion;
    ctx.buildSha = logEnv.buildSha;
    ctx.env = logEnv.env;
  },
};

// ---------------------------------------------------------------------------
// Per-async-call correlationId scope.
// Stack-based; cheap; no Zone polyfill. Callers wrap their async work in
// `correlationScope.run(id, () => ...)` so any log emitted inside picks it up.
// ---------------------------------------------------------------------------

const correlationStack: string[] = [];

export const correlationScope = {
  current(): string | null {
    return correlationStack[correlationStack.length - 1] ?? null;
  },
  push(id: string): void {
    correlationStack.push(id);
  },
  pop(): void {
    correlationStack.pop();
  },
  /**
   * Run `fn` with `id` as the active correlationId. Works for sync + async fns.
   * Note: if the async fn awaits something that itself enters another scope,
   * the parent scope is correctly restored after the await chain resolves.
   */
  run<T>(id: string, fn: () => T): T {
    correlationStack.push(id);
    try {
      const result = fn();
      if (result && typeof (result as unknown as Promise<unknown>).then === 'function') {
        return (result as unknown as Promise<unknown>).finally(() => {
          // Pop the same depth we pushed; safe even if nested scopes pushed more.
          const idx = correlationStack.lastIndexOf(id);
          if (idx >= 0) correlationStack.splice(idx, 1);
        }) as unknown as T;
      }
      correlationStack.pop();
      return result;
    } catch (err) {
      correlationStack.pop();
      throw err;
    }
  },
};
